import { HTTP_INTERCEPTORS, provideHttpClient, withInterceptors, withInterceptorsFromDi } from '@angular/common/http';
import { LocationStrategy, PathLocationStrategy, registerLocaleData } from '@angular/common';
import { DEFAULT_CURRENCY_CODE, LOCALE_ID, NgModule } from '@angular/core';
import localeExtraPt from '@angular/common/locales/extra/pt';
import { BrowserModule } from '@angular/platform-browser';
import { CookieService } from 'ngx-cookie-service';
import localePt from '@angular/common/locales/pt';
import { AuthModule } from '@auth0/auth0-angular';
import { provideLottieOptions } from 'ngx-lottie';
import { provideIza, IZA_INTERCEPTORS, IZA_CNPJ_STREAM, IZA_REQUEST_CNPJ_SELECTION } from '@rmfarma/iza/core';
import { map } from 'rxjs';

import { CnpjService } from './core/services/common/cnpj.service';

import { PharmaSyncComponent } from "./core/components/pharma-sync/pharma-sync.component";
import { provideAnimationsAsync } from '@angular/platform-browser/animations/async';
import { PopUpCnpjComponent } from './shared/pop-up-cnpj/pop-up-cnpj.component';
import { ToastComponent } from './shared/notifications/toast/toast.component';
import { authInterceptor } from './core/interceptors/auth.interceptor';
import { DateInterceptor } from './core/api/date.interceptor';
import { environment } from '../environments/environment';
import { AppRoutingModule } from './app-routing.module';
import { SharedModule } from './shared/shared.module';
import { AppComponent } from './app.component';
import { Router } from '@angular/router';

import { HeaderComponent } from "./core/components/header/components/header/header.component";
import { GuidedTourComponent } from './shared/guided-tour/components/guided-tour/guided-tour.component';
import { ShepherdService } from 'angular-shepherd';
import { FreemiumAcabandoComponent } from './core/components/freemium-acabando/freemium-acabando.component';
import { IzaBubbleHostComponent } from './iza-bubble-host/iza-bubble-host.component';
import { NotificationsComponent } from './core/components/notifications/notifications.component';


registerLocaleData(localePt, 'pt', localeExtraPt)

@NgModule({
  declarations: [
    AppComponent,
  ],
  bootstrap: [AppComponent],
  imports: [
    BrowserModule,
    AppRoutingModule,
    AuthModule.forRoot({
        domain: environment.yourDomain,
        clientId: environment.clientId,
        authorizationParams: {
            redirect_uri: window.location.origin
        },
        useRefreshTokens: true,
        cacheLocation: 'localstorage',
        // SSO por cookie só vale entre subdominios grupohipersaude; nos demais
        // dominios (app.otimizafarma.com.br) o cookie seria rejeitado pelo browser.
        cookieDomain: location.hostname.endsWith('grupohipersaude.com.br')
            ? '.grupohipersaude.com.br'
            : undefined
    }),
    SharedModule,
    ToastComponent,
    PopUpCnpjComponent,
    PharmaSyncComponent,
    HeaderComponent,
    GuidedTourComponent,
    FreemiumAcabandoComponent,
    IzaBubbleHostComponent,
    NotificationsComponent
],
  providers: [
    CookieService,
    provideHttpClient(
      withInterceptors([authInterceptor, ...IZA_INTERCEPTORS]),
      withInterceptorsFromDi()
    ),
    provideIza({
      ahriApiUrl: environment.AHRI_API_URL,
      autosync: { apiUrl: environment.API_URL_AUTOSYNC, token: environment.TOKEN_AUTOSYNC },
      pharmaDashApiUrl: environment.urlApiPharmDash,
    }),
    {
      // Otimiza é a fonte da verdade do CNPJ; a Iza apenas reflete a seleção do host.
      provide: IZA_CNPJ_STREAM,
      useFactory: (cnpj: CnpjService) => cnpj.selectedCnpj$.pipe(map(v => v || null)),
      deps: [CnpjService],
    },
    {
      // Clicar no balão sem CNPJ abre o seletor do próprio Otimiza.
      provide: IZA_REQUEST_CNPJ_SELECTION,
      useFactory: (cnpj: CnpjService) => () => cnpj.showCnpjPopup(true),
      deps: [CnpjService],
    },
    provideLottieOptions({
      player: () => import(/* webpackChunkName: 'lottie-web' */ 'lottie-web'),
    }),
    {
      provide: HTTP_INTERCEPTORS,
      useClass: DateInterceptor,
      multi: true,
    },
    {
      provide: LocationStrategy,
      useClass: PathLocationStrategy
    },
    {
      provide: LOCALE_ID,
      useValue: 'pt'
    },
    {
      provide: DEFAULT_CURRENCY_CODE,
      useValue: 'BRL'
    },
    provideAnimationsAsync(),
    ShepherdService
  ]
})
export class AppModule {
  constructor(
    private router: Router
  ) {

    const urlParams = new URLSearchParams(window.location.search);
    const getUtms = localStorage.getItem('utm_params');

    const hasValidUtms = ['utm_source', 'utm_medium', 'utm_campaign', 'utm_term', 'utm_content']
      .some((param) => urlParams.get(param));

    if (urlParams.size > 0 && hasValidUtms) {
      let utms = {
        utm_source: urlParams.get('utm_source'),
        utm_medium: urlParams.get('utm_medium'),
        utm_campaign: urlParams.get('utm_campaign'),
        utm_term: urlParams.get('utm_term'),
        utm_content: urlParams.get('utm_content'),
      };

      localStorage.setItem('utm_params', JSON.stringify(utms));
    }

    const currentUrl = window.location.href;

    // Verifica se já existe um 'savedHref' no localStorage e salva se não existir
    const savedHref = localStorage.getItem('savedHref');
    if (!savedHref) {
      localStorage.setItem('savedHref', currentUrl);
    }

    const currentLocation: Location = window.location;
    const savedLocation = localStorage.getItem('savedLocation');
    if (!savedLocation) {
      localStorage.setItem('savedLocation', JSON.stringify(currentLocation));

      const retrievedLocation = localStorage.getItem('savedLocation');
      if (retrievedLocation) {
        const parsedLocation: Location = JSON.parse(retrievedLocation);
        if (parsedLocation.pathname === '/') {
          // Remove os itens se o pathname for a raiz
          localStorage.removeItem('savedLocation');
          localStorage.removeItem('savedHref');
        }
      }
    } else {
      const parsedLocation: Location = JSON.parse(savedLocation);
      if (parsedLocation.pathname === '/') {
        // Remove os itens se o pathname for a raiz
        localStorage.removeItem('savedLocation');
        localStorage.removeItem('savedHref');
      }
    }
  }
}
